import apiClient from './apiClient';
import { 
  User, 
  Veterinarian, 
  UserFormData, 
  VeterinarianFormData, 
  UserFilters,
  UserRole,
  UserStatus 
} from '@app-types/userManagement';

// Exportăm userManagementApi ca alias pentru apiClient pentru compatibilitate
// Serviciile care folosesc userManagementApi vor funcționa fără modificări
export const userManagementApi = apiClient;

export const userManagementService = {
  // === UTILITY FUNCTIONS ===
  
  // Obtine clinicId-ul utilizatorului curent autentificat
  getCurrentUserClinicId(): string | null {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.clinicId || null;
      }
      return null;
    } catch (error) {
            return null;
    }
  },

  // Curata obiectul de campuri goale/null/undefined (cu exceptii pentru campurile obligatorii)
  cleanObject<T extends Record<string, any>>(obj: T): Partial<T> {
    // Campuri obligatorii care NU trebuie eliminate chiar daca sunt goale
    const requiredFields = ['password', 'email', 'firstName', 'lastName', 'role', 'clinicId'];
    
    const out: Record<string, any> = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v === undefined || v === null) return;          // scoate null/undefined
      if (typeof v === 'string' && v.trim() === '' && !requiredFields.includes(k)) return; // scoate string gol, dar pastreaza campurile obligatorii
      out[k] = v;
    });
    return out as Partial<T>;
  },

  // === USER MANAGEMENT ===
  
  // Obtine lista de utilizatori cu filtrare
  async getUsers(filters?: UserFilters): Promise<User[]> {
    try {
      const params = new URLSearchParams();
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString());
          }
        });
      }

      const response = await userManagementApi.get(`/users?${params.toString()}`);
      return response.data.data || [];
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la incarcarea utilizatorilor');
    }
  },

  // Obtine un utilizator specific
  async getUser(userId: string): Promise<User> {
    try {
      const response = await userManagementApi.get(`/users/${userId}`);
      return response.data.data;
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la incarcarea utilizatorului');
    }
  },

  // Creeaza un utilizator nou
  async createUser(userData: UserFormData): Promise<User> {
    try {
      const response = await userManagementApi.post('/users', userData);
      return response.data.data;
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la crearea utilizatorului');
    }
  },

  // Actualizeaza un utilizator
  async updateUser(userId: string, userData: Partial<UserFormData>): Promise<User> {
    try {
      // Daca exista poza de profil, o incarca mai intai
      if (userData.profilePhoto instanceof File) {
        const photoUrl = await this.uploadFile(userData.profilePhoto);
        userData.profilePhoto = photoUrl;
      }

      // Daca exista CV, il incarca mai intai
      if (userData.cvFile instanceof File) {
        const cvUrl = await this.uploadFile(userData.cvFile);
        userData.cvFile = cvUrl;
      }

      const response = await userManagementApi.patch(`/users/${userId}`, userData);
      return response.data.data;
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la actualizarea utilizatorului');
    }
  },

  // Activeaza un utilizator
  async activateUser(userId: string): Promise<void> {
    try {
      await userManagementApi.patch(`/users/${userId}/status`, { status: UserStatus.ACTIVE });
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la activarea utilizatorului');
    }
  },

  // Dezactiveaza un utilizator
  async deactivateUser(userId: string): Promise<void> {
    try {
      await userManagementApi.patch(`/users/${userId}/status`, { status: UserStatus.INACTIVE });
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la dezactivarea utilizatorului');
    }
  },

  // Incarca un fisier
  async uploadFile(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

            const response = await userManagementApi.post('/users/storage/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

            if (!response.data.success) {
        throw new Error(response.data.error || 'Upload failed');
      }

      if (!response.data.data || !response.data.data.url) {
                throw new Error('Raspuns invalid de la server');
      }

      return response.data.data.url;
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la incarcarea fisierului');
    }
  },

  // Reseteaza parola unui utilizator
  async resetUserPassword(userId: string): Promise<void> {
    try {
      await userManagementApi.post(`/users/${userId}/reset-password`);
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la resetarea parolei');
    }
  },

  // Sterge un utilizator
  async deleteUser(userId: string): Promise<void> {
    try {
      await userManagementApi.delete(`/users/${userId}`);
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la stergerea utilizatorului');
    }
  },

  // === VETERINARIAN MANAGEMENT ===

  // Obtine lista de veterinari
  async getVeterinarians(): Promise<Veterinarian[]> {
    try {
      const response = await userManagementApi.get('/veterinarians');
      return response.data.data || [];
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la incarcarea veterinarilor');
    }
  },

  // Obtine un veterinar specific
  async getVeterinarian(veterinarianId: string): Promise<Veterinarian> {
    try {
      const response = await userManagementApi.get(`/veterinarians/${veterinarianId}`);
      return response.data.data;
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la incarcarea veterinarului');
    }
  },

  // Creeaza un veterinar nou (3 pasi: user + profil veterinar + fisiere)
  async createVeterinarian(veterinarianData: VeterinarianFormData): Promise<User> {
    let userPayload: any = null;
    
    try {
            // PAS 1: Creeaza utilizatorul cu datele de baza (JSON simplu)
      const clinicId = this.getCurrentUserClinicId();
      if (!clinicId) {
        throw new Error('ClinicId lipseste din contextul utilizatorului autentificat');
      }

      userPayload = this.cleanObject({
        firstName: veterinarianData.firstName,
        lastName: veterinarianData.lastName,
        email: veterinarianData.email,
        password: veterinarianData.password,
        role: 'veterinarian',  // exact enum
        status: 'active',
        clinicId: clinicId,    // obligatoriu
        phone: typeof veterinarianData.phone === 'string' ? veterinarianData.phone.trim() : undefined,
      });

      const userResponse = await userManagementApi.post('/users', userPayload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const createdUser = userResponse.data.data;
            // PAS 2: Actualizeaza utilizatorul cu datele specifice veterinarului
      const vetProfilePayload = {
        specializations: veterinarianData.specializations && veterinarianData.specializations.length > 0 
          ? JSON.stringify(veterinarianData.specializations) 
          : null,
        experienceYears: veterinarianData.experienceYears ? parseInt(veterinarianData.experienceYears.toString()) : null,
        description: veterinarianData.description || null
      };

            const updatedUser = await this.updateUser(createdUser.id, vetProfilePayload);
            // PAS 3: Upload fisiere separat (daca exista)
      if (veterinarianData.profilePhoto) {
        try {
                    const photoUrl = await this.uploadProfilePhoto(veterinarianData.profilePhoto);
                    // Actualizeaza utilizatorul cu URL-ul fotografiei
          await this.updateUser(createdUser.id, { profilePhoto: photoUrl });
        } catch (error) {
                    // Nu opreste procesul daca upload-ul foto esueaza
        }
      }

      if (veterinarianData.cvFile) {
        try {
                    const cvUrl = await this.uploadCv(veterinarianData.cvFile);
                    // Actualizeaza utilizatorul cu URL-ul CV-ului
          await this.updateUser(createdUser.id, { cvFile: cvUrl });
        } catch (error) {
                    // Nu opreste procesul daca upload-ul CV esueaza
        }
      }

      return updatedUser;
    } catch (error: any) {
                                                      throw new Error(error.response?.data?.error || error.response?.data?.message || 'Eroare la crearea veterinarului');
    }
  },

  // Actualizeaza un veterinar
  async updateVeterinarian(veterinarianId: string, veterinarianData: Partial<VeterinarianFormData>): Promise<Veterinarian> {
    try {
      const formData = new FormData();
      
      // Adauga doar campurile care au fost modificate
      Object.entries(veterinarianData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'specializations' && Array.isArray(value)) {
            formData.append(key, JSON.stringify(value));
          } else if (value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, value.toString());
          }
        }
      });

      const response = await userManagementApi.patch(`/veterinarians/${veterinarianId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data;
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la actualizarea veterinarului');
    }
  },

  // === FILE UPLOAD ===

  // Upload foto de profil
  async uploadProfilePhoto(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'profile-photo');

      const response = await userManagementApi.post('/users/storage/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data.url;
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la incarcarea fotografiei');
    }
  },

  // Upload CV
  async uploadCv(file: File): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'cv');

      const response = await userManagementApi.post('/users/storage/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.data.url;
    } catch (error: any) {
            throw new Error(error.response?.data?.error || 'Eroare la incarcarea CV-ului');
    }
  },

  // === UTILITY FUNCTIONS ===

  // Obtine rolurile disponibile
  getAvailableRoles(): { value: UserRole; label: string }[] {
    return [
      { value: UserRole.CLINIC_ADMIN, label: 'Administrator Clinica' },
      { value: UserRole.VETERINARIAN, label: 'Veterinar' },
      { value: UserRole.RECEPTIONIST, label: 'Receptioner' },
      { value: UserRole.ACCOUNTANT, label: 'Contabil' },
      { value: UserRole.CLIENT, label: 'Client' },
    ];
  },

  // Obtine statusurile disponibile
  getAvailableStatuses(): { value: UserStatus; label: string }[] {
    return [
      { value: UserStatus.ACTIVE, label: 'Activ' },
      { value: UserStatus.INACTIVE, label: 'Inactiv' },
      { value: UserStatus.PENDING, label: 'In asteptare' },
      { value: UserStatus.SUSPENDED, label: 'Suspendat' },
    ];
  },

  // Obtine specializarile disponibile
  getAvailableSpecializations(): string[] {
    return [
      'Medicina generala veterinara',
      'Chirurgie veterinara',
      'Dermatologie veterinara',
      'Stomatologie veterinara',
      'Oftalmologie veterinara',
      'Cardiologie veterinara',
      'Neurologie veterinara',
      'Oncologie veterinara',
      'Medicina de urgenta veterinara',
      'Medicina exotica',
      'Medicina aviara',
      'Medicina ecvina',
      'Medicina canina si felina',
    ];
  },
};
